import React from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { t } from 'i18next';
import { reduxForm, getFormValues, Field } from 'redux-form';
import { connect } from 'react-redux';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { isRequired, maxChar7, isValidCanadaUSPostalCode } from '../../utils/validator';
import SitecorecontextFactory from '../../lib/SitecoreContextFactory';
import {
  Container,
  Header,
  Form,
  Title,
  Description,
  FormGroup,
  FormGroupTitle,
  CheckboxesContainer,
  ResultsSection,
  ResultsTitle,
  CardsContainer,
  Card,
  CardHeader,
  CardFooter,
  CardBodyTable,
  TableHeadCell,
  TableHeadRow,
  TableBodyRow,
  TableBodyCell,
  SubmitButtonContainer,
  DesktopResults,
  DesktopResultZip,
  DesktopResultHeader,
  DesktopResultCell,
  DesktopResultSideHeader,
  DesktopResultData,
  DesktopResultValue,
  DesktopResultsFooter,
} from './css';

import TextInput from '../../lib/ui/form_elements/TextInput';
import DatePicker from '../../lib/ui/form_elements/DatePicker';
import Checkbox from '../../lib/ui/form_elements/Checkbox';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';
import { fetchResults } from './api';
import { Locales } from '../../utils/enums';
import apiDBErrorMessage from '../../utils/apiDBErrorMessage';
import ModalBoxStyle from '../_styledComponents/ModalBoxStyle';
import { ButtonDiv, ModalBody, ModalTitle } from '../CreateShipment/css';

const DATE_FORMAT_PATTERN = 'yyyy-MM-dd';

const initialValues = {
  serviceLevels: {
    TL: false,
    LTL: true,
    CBLTL: false,
    R1T: false,
  },
  origin: {
    postalZipCode: null,
    pickupDate: format(new Date(), DATE_FORMAT_PATTERN),
  },
  destinations: {
    postalZipCode: null,
    optionalPostalZipCode1: null,
    optionalPostalZipCode2: null,
  },
};

class TransitTimeCalculatorForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...initialValues,
      results: [],
      modalIsOpen: false,
      errorMessage: null,
    };
  }

  handleCalculate = (e) => {
    const { valid, formData } = this.props;

    if (valid) {
      e.preventDefault();
      const serviceLevels = Object.keys(formData.serviceLevels).filter((sl) => formData.serviceLevels[sl]);
      const destinations = Object.values(formData.destinations).filter((d) => d);

      const updateResults = (res) => {
        const updatedState = { ...this.state };
        updatedState.results = res.transitTimes;
        this.setState(updatedState);
      };

      fetchResults({
        serviceLevels,
        origin: formData.origin.postalZipCode,
        pickupDate: formData.origin.pickupDate,
        destinations,
        updateResults,
      }).catch((error) => {
        const errorMessage = error?.errors?.message ? apiDBErrorMessage(error.errors.message) : null;
        this.setState({ modalIsOpen: true, errorMessage: errorMessage || t('ErrorMessage'), getRatesError: true });
      });
    }
  };

  getServiceLevelByCode = (serviceLevel) => {
    const serviceLevelCodes = {
      TL: t('Truckload'),
      LTL: t('DomesticLTL'),
      CBLTL: t('CrossBorderLTL'),
      R1T: t('HomeDelivery'),
    };
    return serviceLevelCodes[serviceLevel] || serviceLevel;
  };

  renderDesktopResults = () => {
    const flattenDetails = this.state.results.map(({ details }) => details).flat(1);
    const rowsByServiceLevel = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const { serviceLevel, transitDays, estimatedDeliveryDate } of flattenDetails) {
      rowsByServiceLevel[serviceLevel] = rowsByServiceLevel[serviceLevel]
        ? [...rowsByServiceLevel[serviceLevel], { transitDays, estimatedDeliveryDate }]
        : [{ transitDays, estimatedDeliveryDate }];
    }

    const rows = Object.keys(rowsByServiceLevel).map((serviceLevel) => ({ serviceLevel, values: rowsByServiceLevel[serviceLevel] }));

    return (
      <>
        {rows.map(({ serviceLevel, values }) => (
          <tr key={serviceLevel}>
            <DesktopResultCell>
              <DesktopResultSideHeader>
                {this.getServiceLevelByCode(serviceLevel)}
              </DesktopResultSideHeader>
            </DesktopResultCell>
            {values.map(({ transitDays, estimatedDeliveryDate }, index) => (
              <DesktopResultCell key={index}>
                <DesktopResultData>
                  <DesktopResultValue>{transitDays > 0 && `${transitDays} ${transitDays === '1' ? t('Day') : t('Days')}`}</DesktopResultValue>
                  <DesktopResultValue>
                    {estimatedDeliveryDate && isValid(new Date(estimatedDeliveryDate))
                      ? format(new Date(estimatedDeliveryDate), 'MMM d, yyyy') : t('TimeTransitDateError')}
                  </DesktopResultValue>
                </DesktopResultData>
              </DesktopResultCell>
            ))}
          </tr>
        ))}
      </>
    );
  };

  closeModal = () => {
    this.setState({
      modalIsOpen: false,
    });
  };

  render() {
    const today = new Date();
    const oneWeekBefore = new Date();
    oneWeekBefore.setDate(today.getDate() - 7);

    const { valid } = this.props;
    const {
      modalIsOpen,
      errorMessage,
    } = this.state;
    registerLocale('fr', fr);
    setDefaultLocale(SitecorecontextFactory.context.language === Locales.English ? 'en' : 'fr');
    return (
      <Container>

        <Modal
          isOpen={modalIsOpen}
          onRequestClose={this.closeModal}
          style={ModalBoxStyle}
          contentLabel="Modal"
        >
          <ModalBody>
            <ModalTitle>{t('Error')}</ModalTitle>
            <p><strong>{errorMessage}</strong></p>
            <p>{t('PleaseContactCustomerService')}</p>
            <ButtonDiv className="row no-gutters">

              <div className="col-sm-12 col-md">
                <PrimaryButton
                  name="cancelButton"
                  label={t('Return')}
                  onClick={() => this.closeModal()}
                />
              </div>
            </ButtonDiv>

          </ModalBody>
        </Modal>

        <Header>
          <Title>{t('TransitTimeCalculator')}</Title>
          <Description>{t('TransitTimeCalculatorDescription')}</Description>
        </Header>

        <Form onSubmit={this.handleCalculate}>

          <FormGroup>
            <FormGroupTitle>{t('ServiceLevel')}</FormGroupTitle>
            <CheckboxesContainer>
              <Field
                name="serviceLevels.TL"
                component={Checkbox}
                label={t('Truckload')}
              />
              <Field
                name="serviceLevels.LTL"
                component={Checkbox}
                label={t('DomesticLTL')}
                defaultChecked
              />
              <Field
                name="serviceLevels.CBLTL"
                component={Checkbox}
                label={t('CrossBorderLTL')}
              />
              <Field
                name="serviceLevels.R1T"
                component={Checkbox}
                label={t('HomeDelivery')}
              />
            </CheckboxesContainer>
          </FormGroup>

          <FormGroup>
            <FormGroupTitle>{t('Origin')}</FormGroupTitle>
            <Field
              name="origin.postalZipCode"
              component={TextInput}
              label={t('PostalZipCode')}
              placeholder="NLM24N"
              validate={[isRequired, maxChar7, isValidCanadaUSPostalCode]}
            />
            <Field
              name="origin.pickupDate"
              component={DatePicker}
              label={t('Date')}
              initialDate={oneWeekBefore}
              validate={[isRequired]}
            />
          </FormGroup>

          <FormGroup>
            <FormGroupTitle>{t('Destinations')}</FormGroupTitle>
            <Field
              name="destinations.postalZipCode"
              component={TextInput}
              label={t('PostalZipCode')}
              placeholder="NLM24N"
              validate={[isRequired, maxChar7, isValidCanadaUSPostalCode]}
            />
            <Field
              name="destinations.optionalPostalZipCode1"
              component={TextInput}
              label={`${t('PostalZipCode')} (${t('Optional')})`}
              placeholder="NLM24N"
              validate={[maxChar7, isValidCanadaUSPostalCode]}
            />
            <Field
              name="destinations.optionalPostalZipCode2"
              component={TextInput}
              label={`${t('PostalZipCode')} (${t('Optional')})`}
              placeholder="NLM24N"
              validate={[maxChar7, isValidCanadaUSPostalCode]}
            />
          </FormGroup>

          <SubmitButtonContainer>
            <PrimaryButton fill label={t('Calculate')} type="submit" disabled={!valid} />
          </SubmitButtonContainer>

        </Form>

        {this.state.results.length > 0
          && (
            <ResultsSection>
              <ResultsTitle>{t('TransitTimeResults')}</ResultsTitle>
              <CardsContainer>
                {this.state.results.map(({ destination, details }, index) => (
                  <Card key={destination + index}>
                    <CardHeader>{destination}</CardHeader>
                    <CardBodyTable>
                      <TableHeadRow>
                        <TableHeadCell>{t('ServiceLevel')}</TableHeadCell>
                        <TableHeadCell>{t('Transit')}</TableHeadCell>
                        <TableHeadCell>{t('EstDelivery')}</TableHeadCell>
                      </TableHeadRow>
                      {details.map(({ serviceLevel, transitDays, estimatedDeliveryDate }) => (
                        <TableBodyRow key={serviceLevel}>
                          <TableBodyCell>{this.getServiceLevelByCode(serviceLevel)}</TableBodyCell>
                          <TableBodyCell>
                            {transitDays}
                            {' '}
                            {transitDays === 1 ? t('Day') : t('Days')}
                          </TableBodyCell>
                          <TableBodyCell>
                            {estimatedDeliveryDate && isValid(new Date(estimatedDeliveryDate))
                              ? format(new Date(estimatedDeliveryDate), 'MMM d, yyyy') : t('TimeTransitDateError')}
                          </TableBodyCell>
                        </TableBodyRow>
                      ))}
                    </CardBodyTable>
                    <CardFooter>
                      *
                      {t('EstDeliveryInfo1')}
                      <br />
                      {t('EstDeliveryInfo2')}
                    </CardFooter>
                  </Card>
                ))}
              </CardsContainer>
              <DesktopResults>
                <thead>
                  <tr>
                    <th>
                      <div>
                        <DesktopResultHeader><h4>{t('ServiceLevel')}</h4></DesktopResultHeader>
                      </div>
                    </th>
                    {this.state.results.map(({ destination }, index) => (
                      <th key={destination + index}>
                        <div>
                          <DesktopResultZip>{destination}</DesktopResultZip>
                          <DesktopResultHeader>
                            <h4>{t('Transit')}</h4>
                            <h4>{t('EstDelivery')}</h4>
                          </DesktopResultHeader>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {this.renderDesktopResults()}
                </tbody>
              </DesktopResults>
              <DesktopResultsFooter>
                *
                {t('EstDeliveryInfo1')}
                {' '}
                {t('EstDeliveryInfo2')}
              </DesktopResultsFooter>
            </ResultsSection>
          )}
      </Container>
    );
  }
}

TransitTimeCalculatorForm.propTypes = {
  valid: PropTypes.bool,
  formData: PropTypes.shape({
    serviceLevels: PropTypes.shape({
      TL: PropTypes.bool,
      LTL: PropTypes.bool,
      CBLTL: PropTypes.bool,
      R1T: PropTypes.bool,
    }),
    origin: PropTypes.shape({
      postalZipCode: PropTypes.string,
      pickupDate: PropTypes.string,
    }),
    destinations: PropTypes.shape({
      postalZipCode: PropTypes.string,
      optionalPostalZipCode1: PropTypes.string,
      optionalPostalZipCode2: PropTypes.string,
    }),
  }),
};

TransitTimeCalculatorForm = reduxForm({
  form: 'TransitTimeCalculatorForm',
  initialValues,
})(TransitTimeCalculatorForm);

const mstp = (state) => ({
  formData: getFormValues('TransitTimeCalculatorForm')(state),
  isAuthenticated: state.profile.isAuthenticated,
  division: state.profile.division,
  completed: Boolean,
});

export default connect(mstp)(TransitTimeCalculatorForm);
